import paper from "paper";

export class Viewport {
  scope: paper.PaperScope;
  project: paper.Project;
  canvas: HTMLCanvasElement;
  private zoomChangeListeners = new Set<(z: number) => void>();
  /** Where the canvas's top-left corner sat on the page at the last sync.
   *  A resize usually moves that corner too (the toolbar expanding widens its
   *  column and pushes the canvas right), and the anchor below has to tell the
   *  two apart. Null until the first sync. */
  private lastCorner: { x: number; y: number } | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.scope = new paper.PaperScope();
    this.scope.setup(canvas);
    this.project = this.scope.project;
    this.scope.view.onResize = () => this.emitZoom();
    // Paper.js' `resize="true"` attribute only reacts to window resize, so a
    // layout-driven canvas resize (e.g. toggling the sidebar's collapse
    // state changes the grid column widths) leaves the view's internal
    // viewSize stale and shifts pointer / draw coordinates. A ResizeObserver
    // on the canvas itself catches every layout change and re-syncs the
    // view, keeping the zoom and the drawing's place on screen (#1046).
    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(() => this.syncViewSize());
      observer.observe(canvas);
    }
  }

  /**
   * Re-sync the paper view to the canvas element's current size WITHOUT moving
   * the drawing on screen (#1046).
   *
   * The invariant is the page↔project mapping: a point the user can see stays
   * under the same pixel. Anchoring the project point at the canvas's top-left
   * corner does that, once it is offset by however far that corner itself
   * moved on the page:
   *
   *     bounds.left' = bounds.left + (cornerX' - cornerX) / zoom
   *
   * which covers both ways the canvas can change. A side panel opens on the
   * right: the corner doesn't move, so nothing moves and the canvas simply has
   * less room out to the right. The toolbar expands on the left: the corner
   * moves right, the anchor moves with it, and again nothing moves.
   *
   * Anchoring the CENTRE instead — what this did before — is only stable for a
   * resize that grows symmetrically about that centre, and none of ours do.
   * The canvas is a `1fr` grid column against a fixed left edge, so the whole
   * width change lands on the right; holding the centre fixed dragged every
   * object left by half of it. One 280px panel moved the drawing 140px.
   *
   * It also disagreed with paper's own behaviour on this very canvas. The
   * element carries `resize="true"`, so paper installs a window-resize handler
   * that anchors the top-left (it drops the cached bounds and leaves the view
   * matrix alone). On a window resize that handler lands first and the check
   * below returns early, so the centre branch never ran there — which is why
   * only the layout-driven resizes, the ones with no window event behind them,
   * ever moved the drawing. Both paths now do the same thing.
   */
  private syncViewSize() {
    const w = this.canvas.clientWidth;
    const h = this.canvas.clientHeight;
    if (w <= 0 || h <= 0) return;
    const rect = this.canvas.getBoundingClientRect();
    const prev = this.lastCorner;
    // Recorded on every call, including the early return below: the corner can
    // move without the size changing, and a stale reading would be charged to
    // the next real resize as a phantom shift.
    this.lastCorner = { x: rect.left, y: rect.top };
    const cur = this.scope.view.viewSize;
    // Skip the assignment when nothing actually changed — paper.js would
    // emit onResize and re-render unnecessarily otherwise.
    if (Math.abs(cur.width - w) < 0.5 && Math.abs(cur.height - h) < 0.5) {
      return;
    }
    const zoom = this.scope.view.zoom;
    const topLeft = this.scope.view.bounds.topLeft.clone();
    const dx = prev ? (rect.left - prev.x) / zoom : 0;
    const dy = prev ? (rect.top - prev.y) / zoom : 0;
    this.scope.view.viewSize = new this.scope.Size(w, h);
    // Paper.js doesn't reliably preserve either zoom or position across a
    // viewSize change, so both are reasserted. Centre is the only setter for
    // the view's position, hence the half-a-viewport arithmetic back from the
    // corner we actually want to pin.
    this.scope.view.zoom = zoom;
    this.scope.view.center = new this.scope.Point(
      topLeft.x + dx + w / zoom / 2,
      topLeft.y + dy + h / zoom / 2,
    );
  }

  get view(): paper.View {
    return this.scope.view;
  }

  get zoom(): number {
    return this.view.zoom;
  }

  set zoom(z: number) {
    this.view.zoom = Math.max(0.04, Math.min(50, z));
    this.ensureContentInView();
    this.emitZoom();
  }

  /** Recover the view after a centred zoom change leaves the canvas
   *  empty (#362). The zoom-indicator presets and its double-click
   *  1:1/Fit toggle pivot about the view centre without moving it, so
   *  content that had been panned to the edge can end up entirely
   *  off-screen. When that happens — and only then — slide the view
   *  (pan only, the user's chosen scale is preserved) so the content
   *  fills the visible area, leaving just a `marginPx` gap on the side
   *  the view had been zoomed into.
   *
   *  Per axis: the content's edge nearest the old view is pulled to
   *  `marginPx` inside that same edge, so the content occupies the
   *  rest of the view and (when larger than the view) overflows the
   *  far side. Only the axis the content is fully off on is touched.
   *
   *  "Content" is the active layer's bounds: every rendered item plus
   *  the cutting mat. A no-op when the content is empty or already
   *  intersects the visible area, so normal zooming is never
   *  disturbed. */
  ensureContentInView(marginPx = 24) {
    const content = this.project.activeLayer?.bounds;
    if (!content || content.width === 0 || content.height === 0) return;
    const vb = this.view.bounds;
    if (vb.intersects(content)) return;
    const m = marginPx / this.view.zoom;
    const halfW = vb.width / 2;
    const halfH = vb.height / 2;
    let cx = this.view.center.x;
    let cy = this.view.center.y;
    if (content.x > vb.x + vb.width) {
      // Content off toward +x; leave the gap on that (zoomed-into)
      // side by pulling the content's left edge in from the far side.
      cx = content.x - m + halfW;
    } else if (content.x + content.width < vb.x) {
      // Content off toward -x; gap on the -x side, content's right
      // edge sits `m` inside the near edge.
      cx = content.x + content.width + m - halfW;
    }
    if (content.y > vb.y + vb.height) {
      cy = content.y - m + halfH;
    } else if (content.y + content.height < vb.y) {
      cy = content.y + content.height + m - halfH;
    }
    this.view.center = new this.scope.Point(cx, cy);
  }

  pan(dx: number, dy: number) {
    this.view.center = new this.scope.Point(
      this.view.center.x - dx / this.view.zoom,
      this.view.center.y - dy / this.view.zoom,
    );
  }

  zoomAt(viewPt: { x: number; y: number }, factor: number) {
    const before = this.view.viewToProject(
      new this.scope.Point(viewPt.x, viewPt.y),
    );
    const newZoom = Math.max(0.04, Math.min(50, this.view.zoom * factor));
    this.view.zoom = newZoom;
    const after = this.view.viewToProject(
      new this.scope.Point(viewPt.x, viewPt.y),
    );
    this.view.center = new this.scope.Point(
      this.view.center.x + (before.x - after.x),
      this.view.center.y + (before.y - after.y),
    );
    this.emitZoom();
  }

  fitToContent(margin = 40) {
    const bounds = this.project.activeLayer?.bounds;
    if (!bounds || bounds.width === 0 || bounds.height === 0) {
      this.view.center = new this.scope.Point(0, 0);
      this.view.zoom = 1;
      this.emitZoom();
      return;
    }
    this.fitToBounds(bounds, margin);
  }

  /** Center + zoom so the given project-space rectangle fills the view. */
  fitToBounds(
    bounds: {
      x: number;
      y: number;
      width: number;
      height: number;
      center?: paper.Point;
    },
    margin = 40,
  ) {
    if (!bounds || bounds.width === 0 || bounds.height === 0) return;
    const viewW = this.view.viewSize.width;
    const viewH = this.view.viewSize.height;
    const sx = (viewW - margin * 2) / bounds.width;
    const sy = (viewH - margin * 2) / bounds.height;
    this.view.zoom = Math.max(0.04, Math.min(50, Math.min(sx, sy)));
    this.view.center =
      bounds.center ??
      new this.scope.Point(
        bounds.x + bounds.width / 2,
        bounds.y + bounds.height / 2,
      );
    this.emitZoom();
  }

  viewToProject(p: { x: number; y: number }): paper.Point {
    return this.view.viewToProject(new this.scope.Point(p.x, p.y));
  }

  onZoomChange(fn: (z: number) => void): () => void {
    this.zoomChangeListeners.add(fn);
    return () => this.zoomChangeListeners.delete(fn);
  }

  private emitZoom() {
    for (const fn of this.zoomChangeListeners) fn(this.view.zoom);
  }

  activate() {
    this.scope.activate();
  }
}
